import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Clock } from "lucide-react";
import { Nav, Footer } from "../App";
import { Eyebrow, PillarAccent } from "../components/atoms";
import { useContactPanel } from "../components/ContactPanel";
import { api } from "../admin/api";

/* ============================================================
   /blog/:slug — Editorial post page
   Premium long-form layout. 1–2 inline images max.
   Calm mask-reveal grammar consistent with the rest of the site.
============================================================ */

/* Render a single content block from the structured `body` array. */
const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2
          data-reveal="mask-block"
          className="font-display text-white font-black tracking-[-0.025em] leading-[1.1] text-[1.6rem] sm:text-[1.9rem] md:text-[2.25rem] mt-16 mb-6"
        >
          <span>{block.text}</span>
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display text-white font-bold tracking-[-0.015em] leading-[1.2] text-[1.15rem] md:text-[1.35rem] mt-10 mb-4">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p className="text-[1.075rem] md:text-[1.15rem] text-neutral-300 leading-[1.8] my-6">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="my-7 space-y-3 pl-0">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="relative pl-7 text-[1.025rem] md:text-[1.1rem] text-neutral-300 leading-[1.75]"
            >
              <span
                aria-hidden
                className="absolute left-0 top-[0.85em] w-3 h-px bg-[#F4D64E]"
              />
              {item}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="my-7 space-y-4 pl-0 counter-reset-blog">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="relative pl-12 text-[1.025rem] md:text-[1.1rem] text-neutral-300 leading-[1.75]"
            >
              <span className="absolute left-0 top-[0.05em] text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              {item}
            </li>
          ))}
        </ol>
      );
    case "image":
      return (
        <figure className="my-14 md:my-20 -mx-2 md:-mx-6">
          <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06]">
            <img
              src={block.src}
              alt={block.alt || ""}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          {block.caption ? (
            <figcaption className="mt-4 text-[0.78rem] tracking-[0.04em] text-neutral-500 italic">
              {block.caption}
            </figcaption>
          ) : null}
        </figure>
      );
    case "pull":
      return (
        <blockquote className="my-12 md:my-16 border-l-2 border-[#F4D64E] pl-6 md:pl-8">
          <p className="font-display text-white font-black tracking-[-0.02em] leading-[1.2] text-[1.55rem] sm:text-[1.85rem] md:text-[2.15rem]">
            <span className="text-[#F4D64E]">&ldquo;</span>
            {block.text}
            <span className="text-[#F4D64E]">&rdquo;</span>
          </p>
        </blockquote>
      );
    case "cta":
      return (
        <p className="my-8">
          <a
            href={block.href}
            className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
          >
            {block.text}
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </p>
      );
    default:
      return null;
  }
};

/* ============================================================
   01 · Hero
============================================================ */
const PostHero = ({ post }) => (
  <section
    data-testid="post-hero"
    className="relative overflow-hidden bg-[#0B0F14] border-b border-white/[0.06]"
  >
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(900px 600px at 80% 20%, rgba(244,214,78,0.10), transparent 60%)",
      }}
    />
    <span
      aria-hidden
      className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
        backgroundSize: "3px 3px",
      }}
    />

    <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-14 md:pb-20">
      {/* Breadcrumb */}
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12"
      >
        <Link to="/" className="hover:text-[#F4D64E] transition-colors">
          Sunday
        </Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <Link to="/blog" className="hover:text-[#F4D64E] transition-colors">
          Blog
        </Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E] truncate max-w-[16ch] sm:max-w-[26ch]">
          {post.category}
        </span>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap items-center gap-5 mb-7">
        <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
          {post.n}
        </span>
        <span className="w-px h-3 bg-white/[0.15]" />
        <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          {post.category}
        </span>
        <span className="w-px h-3 bg-white/[0.15]" />
        <span className="inline-flex items-center gap-2 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-400">
          <Clock className="w-3 h-3 text-[#F4D64E]" />
          {post.readTime}
        </span>
      </div>

      {/* Title */}
      <h1
        data-testid="post-title"
        className="font-display text-white font-black tracking-[-0.035em] leading-[1.02] text-[2.2rem] sm:text-[3rem] md:text-[3.75rem] lg:text-[4.5rem]"
      >
        {post.title}
      </h1>

      {/* Excerpt */}
      <p className="mt-7 max-w-[62ch] text-[1.075rem] md:text-[1.2rem] text-neutral-400 leading-[1.65]">
        {post.excerpt}
      </p>
    </div>

    {/* Cover image */}
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
      <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-white/[0.06]">
        <img
          src={post.coverImg}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <span
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/30 via-transparent to-transparent"
        />
      </div>
    </div>
  </section>
);

/* ============================================================
   02 · Body
============================================================ */
const PostBody = ({ post }) => (
  <article
    data-testid="post-body"
    className="relative bg-[#0B0F14]"
  >
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
      {post.body.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  </article>
);

/* ============================================================
   03 · Inline CTA band (minimal)
============================================================ */
const InlineCTA = ({ onContact }) => (
  <section
    data-testid="post-cta"
    className="relative bg-[#0F141A] border-y border-white/[0.06] overflow-hidden"
  >
    <span
      aria-hidden
      className="absolute -top-20 -right-20 w-[28rem] h-[28rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
    />
    <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-8 items-center">
        <div className="lg:col-span-8">
          <Eyebrow>Got a goal?</Eyebrow>
          <h2 className="mt-6 font-display text-white font-black tracking-[-0.03em] leading-[1.05] text-[1.7rem] sm:text-[2.2rem] md:text-[2.75rem]">
            <span>
              Apply this to{" "}
              <span className="italic font-normal text-neutral-400">your</span>{" "}
              <PillarAccent>brand</PillarAccent>.
            </span>
          </h2>
          <p className="mt-5 max-w-[58ch] text-[1rem] md:text-[1.05rem] text-neutral-400 leading-[1.7]">
            Reading ideas is one thing. Shipping them is another. We help
            ecommerce brands turn frameworks into measurable growth.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-wrap items-center gap-4 lg:justify-end">
          <button
            type="button"
            onClick={onContact}
            data-testid="post-cta-button"
            className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
          >
            Send a brief
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   04 · Related posts
============================================================ */
const RelatedPosts = ({ related = [] }) => {
  if (!related.length) return null;
  return (
    <section
      data-testid="post-related"
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between gap-6 mb-12 md:mb-14">
          <div>
            <Eyebrow>More articles</Eyebrow>
            <h2 className="mt-6 font-display text-white font-black tracking-[-0.035em] leading-[1.05] text-[1.85rem] sm:text-[2.4rem] md:text-[3rem]">
              <span>
                Keep{" "}
                <span className="italic font-normal text-neutral-400">
                  reading
                </span>
                <PillarAccent>.</PillarAccent>
              </span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors shrink-0"
            data-testid="post-back-to-blog"
          >
            All articles
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {related.map((p) => (
            <li key={p.slug} data-testid={`post-related-${p.slug}`}>
              <Link to={`/blog/${p.slug}`} className="group block">
                <div className="relative aspect-[5/4] overflow-hidden rounded-xl border border-white/[0.06] mb-5">
                  <img
                    src={p.coverImg}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/30 rounded-xl transition-all duration-500"
                  />
                  <span className="absolute top-4 left-4 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums bg-[#0B0F14]/70 backdrop-blur px-2 py-1 rounded">
                    {p.n}
                  </span>
                </div>
                <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-2">
                  {p.category}
                </div>
                <h3 className="font-display text-[1.1rem] md:text-[1.25rem] font-black tracking-[-0.015em] leading-[1.2] text-white group-hover:text-[#F4D64E] transition-colors">
                  {p.title}
                </h3>
                <div className="mt-4 inline-flex items-center gap-2 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
                  <Clock className="w-3 h-3" />
                  {p.readTime}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null); // null = loading, false = not found
  const [related, setRelated] = useState([]);
  const { open: openContact } = useContactPanel();

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    api
      .blogGet(slug)
      .then((p) => {
        if (cancelled) return;
        if (!p || !p.published) {
          setPost(false);
        } else {
          setPost(p);
          document.title = `${p.title} — Sunday Digital`;
        }
      })
      .catch(() => {
        if (!cancelled) setPost(false);
      });
    api
      .blogList()
      .then((all) =>
        cancelled
          ? null
          : setRelated(
              (all || [])
                .filter((p) => p.published && p.slug !== slug)
                .slice(0, 3)
            )
      )
      .catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [post]);

  if (post === null) {
    return (
      <div className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body" data-variant="v5">
        <Nav />
        <section className="bg-[#0B0F14] py-32 text-center">
          <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            Loading…
          </span>
        </section>
        <Footer />
      </div>
    );
  }
  if (post === false) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div
      data-testid="blog-post-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <PostHero post={post} />
        <PostBody post={post} />
        <InlineCTA onContact={openContact} />
        <RelatedPosts related={related} />

        {/* Back to blog */}
        <section className="bg-[#0B0F14] border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <Link
              to="/blog"
              data-testid="post-back-link"
              className="group inline-flex items-center gap-3 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-400 hover:text-[#F4D64E] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
              Back to all articles
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* List of slugs is now sourced live from the API */
